<template>
	<ModalHeader class="modal-header" :title="getString('add_existent_products')" :button-text="getString('add')" @click-button="emitSelected" :button-loading="false" />
	<div class="mini-menu-container">
		<div class="mini-menu">
			<div class="mini-menu-header">
				<div class="menu-selector"><AppSelect relative ref="menu-selector" :values="menus" :selected-index="menuIndex" @selected="changeSelectedMenu" /></div>
			</div>
			<div class="mini-menu-body">
				<div class="mini-menu-categories" v-if="selectedMenu && currMenuCategories.length > 0">
					<div v-for="category in currMenuCategories" class="mini-menu-category" :key="category.menu_category_id" :style="'--category-color: '+category.color+';--category-text-color: '+categoryTextColor(category.color)">
						<div class="category-header-container">
							<div class="category-header">
								<div class="category-availability" @click="categoryClick(category)" ><AppCheckbox :is-active="isCategorySelected(category)" :auto-toggle="false" /></div>
								<div class="category-name-container">
									<div class="category-name" @click="toggleDropdown(category)">
										<h3>{{ category.name }}</h3>
										<div class="category-items-num" :class="{'no-items': categoryItems(category).length === 0}">
											<span v-if="categoryItems(category).length > 0">{{ categoryItems(category).length }}</span>
										</div>
										<div class="category-items-selected-num" :class="{active: !!selectedQuantityCategory(category.menu_category_id)}">
											<span>{{ selectedQuantityCategory(category.menu_category_id) }}</span>
										</div>
									</div>
								</div>
								<div class="category-chevron" @click="toggleDropdown(category)"><AppChevronIcon :direction="category.dropdownActive ? 'up' : 'down'" /></div>
							</div>
						</div>
						<div class="category-body">
							<AppDropdown square-corner :collapsed="!category.dropdownActive" white>
								<div class="items-container" v-if="category.items && categoryItems(category).length > 0">
									<div class="item" v-for="item in categoryItems(category)" :key="item.menu_item_id" @click="itemClick(item)">
										<div class="item-availability">
											<AppCheckbox :auto-toggle="false" :is-active="item.listSelected" />
										</div>
										<div class="item-name"><p>{{ item.name }}</p></div>
										<div v-if="item.available === null" class="icon-availability-container" v-html="getIcon('iconHourglass')"></div>
									</div>
								</div>
								<div class="no-items-container" v-else>
									<p>{{ getString("no_items_in_category") }}</p>
								</div>
							</AppDropdown>
						</div>
					</div>
				</div>
				<div class="no-categories" v-if="selectedMenu && currMenuCategories.length === 0">
					<p>{{ getString("no_items_for_lists", [userStore.menuWord]) }}</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import AppSelect from "@/shared/components/AppSelect.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppCheckbox from "@/shared/components/AppCheckbox.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import {useLanguageStore, useSvgStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore, useUserStore} from "@/console-new/stores";
import {isTextColorDark} from "@/shared/helpers/ui";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let menuStore = useMenuStore();
		let notificationStore = useNotificationStore();
		return {getString: languageStore.getString, getIcon: useSvgStore().get, userStore, menuStore, notificationStore};
	},
	name: "MiniMenu",
	components: {AppBottomSheet, ModalHeader, AppChevronIcon, AppCheckbox, AppDropdown, AppButton, AppSelect},
	emits: ["selected-items"],
	props: {
		itemTempId: {
			type: String,
			required: true
		},
		categoryTempId: {
			type: String,
			required: true
		},
		listTempId: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			menusCopy: [],
			menuIndex: 0
		}
	},
	computed: {
		menus() {
			return this.menusCopy.map(menu => menu.name);
		},
		selectedMenu() {
			return this.menusCopy.length > 0 ? this.menusCopy[this.menuIndex] : null;
		},
		currMenuCategories() {
			let finalCategories = this.selectedMenu && this.selectedMenu.categories ? Object.values(this.selectedMenu.categories).sort((a, b) => a.order_number - b.order_number).sort((a, b) => a.variations_only ? -1 : 0) : [];
			return finalCategories.filter(c => c.items && this.categoryItems(c).length > 0);
		},
		menuItem() {
			return this.menuStore.modItemFromTempIds(this.categoryTempId, this.itemTempId);
		},
		list() {
			return this.menuItem.lists[this.listTempId];
		}
	},
	methods: {
		changeSelectedMenu(index) {
			this.menuIndex = index;
		},
		categoryItems(category) {
			return Object.values(category.items).filter(i => !i.deleted_item && i.menu_item_id !== this.menuItem.menu_item_id && !this.hasLinkedListProducts(i.menu_item_id)).sort((a, b) => a.order_number - b.order_number);
		},
		itemClick(item) {
			item.listSelected = !item.listSelected;
		},
		selectedQuantityCategory(catId) {
			let selected = 0;
			let items = catId ? this.selectedMenu.categories[catId]?.items : {};
			for(let item in items) {
				if(items[item].listSelected) {
					selected++;
				}
			}
			return selected;
		},
		isCategorySelected(category) {
			let items = this.categoryItems(category);
			return items.every(item => item.listSelected);
		},
		categoryClick(category) {
			let newState = !this.isCategorySelected(category);
			Object.values(category.items).forEach(item => {
				if(!this.hasLinkedListProducts(item.menu_item_id) && !item.deleted_item && item.menu_item_id !== this.menuItem.menu_item_id) {
					item.listSelected = newState;
				}
			});
		},
		categoryTextColor(color) {
			if(isTextColorDark(color)) {
				return "#000000";
			} else {
				return "#ffffff";
			}
		},
		toggleDropdown(category) {
			this.currMenuCategories.forEach(c => {
				if(c.menu_category_id !== category.menu_category_id) {
					c.dropdownActive = false;
				} else {
					c.dropdownActive = !c.dropdownActive;
				}
			});
		},
		emitSelected() {

			let selectedItems = [];
			this.menusCopy.forEach(menu => {
				Object.values(menu.categories).forEach(category => {
					category.dropdownActive = false;
					Object.values(category.items).forEach(item => {
						if(item.listSelected) {
							selectedItems.push(item);
						}
					});
				});
			});
			this.$emit("selected-items", selectedItems);

		},
		hasLinkedListProducts(itemId) {
			if(!this.list.list_products || !Object.values(this.list.list_products).length) { return false; }
			let returning = false;
			Object.values(this.list.list_products).forEach(listProd => {
				if(listProd.menu_item_linked_id && Number(listProd.menu_item_linked_id) === Number(itemId) && !listProd.deleted_product) {
					returning = true;
				}
			});
			return returning;
		},
		initMenus() {
			this.menusCopy = Object.values(Object.assign({}, this.menuStore.menus));
		},
		refreshSelected() {
			this.menusCopy.forEach(menu => {
				Object.values(menu.categories).forEach(category => {
					Object.values(category.items).forEach(item => {
						item.listSelected = false;
					});
				});
			});

		}
	},
	mounted() {
		this.refreshSelected();
	},
	watch: {
		"menuStore.menus": {
			handler(newVal, oldVal) {
				if(newVal !== null && newVal !== oldVal) {
					this.initMenus();
				}
			},
			immediate: true
		}
	}
}
</script>

<style lang="scss" scoped>
@keyframes availableColorTimetables {
	0% {
		fill: #FDDE00;
	}
	100% {
		fill: #a28e09;
	}
}
.modal-header {
	margin-bottom: 0;
}
.mini-menu-container {
	margin: 0 auto;
	max-width: 100%;
	width: 100%;
	background-color: var(--color-bg);
	border-radius: 13px;
	height: calc(100% - 2 * var(--main-padding));
	overflow: hidden;
	--mini-menu-padding: 20px;

	@media (max-width: 900px) {
		--mini-menu-padding: 12px;
	}

	.mini-menu {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 0 20px 20px;

		.mini-menu-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			width: 100%;
			flex-wrap: wrap;
			gap: 8px;
			padding: var(--mini-menu-padding);
			padding-left: 0;

			.menu-selector {
				margin-right: 10px;
			}

			.save-container {
				margin-left: auto;
			}
		}

		.mini-menu-body {
			overflow-y: auto;
			padding: 2px;
			margin: -2px;

			.mini-menu-categories {
				width: 100%;
				box-shadow: 0 1px 2px var(--color-box-shadow-3);
				border-radius: 13px;
				overflow: hidden;

				.mini-menu-category {
					width: 100%;

					.category-header-container {

						.category-header {
							width: 100%;
							display: flex;
							align-items: stretch;
							border-bottom: 1px solid var(--color-alpha-2);

							.category-availability {
								width: 45px;
								display: flex;
								justify-content: center;
								align-items: center;
								user-select: none;
							}

							.category-name-container {
								@include noSelect;
								width: calc(100% - 75px);
								min-height: 36px;

								.category-name {
									width: 82%;
									min-height: 36px;
									padding: 8px 33px 8px 10px;
									background-color: var(--category-color);
									position: relative;
									cursor: pointer;
									display: flex;
									align-items: center;

									&:after {
										content: "";
										display: block;
										width: 40px;
										height: 100%;
										position: absolute;
										top: 0;
										right: -10px;
										background-color: var(--category-color);
										transform: skew(-15deg);
										border-radius: 5px;
									}

									h3 {
										font-size: 16px;
										color: var(--category-text-color);
										font-weight: 400;
										overflow: hidden;
										text-overflow: ellipsis;
										white-space: nowrap;
									}



									.category-items-num {
										@include noSelect;
										width: 24px;
										height: 16px;
										position: absolute;
										top: calc(50% - 8px);
										right: 2px;
										transform: scale(1) skew(-15deg);
										display: flex;
										justify-content: center;
										align-items: center;
										box-shadow: inset 0 1px 2px var(--color-alpha-4);
										border-radius: 5px;
										background: var(--color-bg);
										z-index: 10;
										transition: transform 0.4s $transition;

										&.no-items {
											background-color: var(--category-color);
											box-shadow: unset;
											transform: scale(0) skew(-15deg);
										}

										span {
											font-size: 11px;
											color: var(--color-txt);
										}

										.items-num-container {
											height: 100%;
											padding: 2px;
											display: flex;
											align-items: center;
											justify-content: center;

											:deep(svg) {
												height: 100%;

												.fill {
													fill: var(--category-color);
												}

												.stroke {
													stroke: var(--category-color);
												}
											}
										}
									}

									.category-items-selected-num {
										@include noSelect;
										width: 24px;
										height: 16px;
										position: absolute;
										top: calc(50% - 8px);
										right: -9px;
										z-index: 0;
										transform: scale(1) skew(-15deg);
										display: flex;
										justify-content: center;
										align-items: center;
										box-shadow: inset 0 1px 2px var(--color-alpha-4);
										border-radius: 0 5px 5px 0;
										background: var(--color-alpha-1);
										transition: transform 0.4s $transition, right 0.4s $transition;

										&.active {
											right: -34px;
										}

										span {
											font-size: 11px;
										}
									}


								}

							}

							.category-chevron {
								width: 30px;
								display: flex;
								justify-content: center;
								align-items: center;
								background-color: var(--color-bg-primary-2);
								cursor: pointer;
								user-select: none;
							}
						}
					}

					.category-body {

						:deep(.dropdown-content) {
							background: transparent;
						}

						.no-items-container {
							margin-bottom: 10px;

							p {
								font-size: 14px;
							}
						}

						.items-container {
							margin-bottom: 10px;
							box-shadow: 0 1px 2px var(--color-box-shadow-3);
							border-radius: 13px;
							overflow: hidden;

							.item {
								display: flex;
								align-items: center;
								border-bottom: 1px solid var(--color-border-alpha);
								cursor: pointer;
								align-items: center;
								@include noSelect;
								background-color: var(--color-bg-primary-1);


								.item-availability {
									width: 45px;
									display: flex;
									justify-content: center;
									align-items: center;
									user-select: none;
								}

								.icon-availability-container {
									width: 12px;
									height: 12px;
									display: flex;
									justify-content: center;
									align-items: center;

									:deep(svg) {
										.fill {
											animation: availableColorTimetables 1.5s linear 0s infinite alternate;
										}
									}
								}

								.item-name {
									width: calc(100% - 70px);
									min-height: 36px;
									display: flex;
									align-items: center;
									padding-right: 8px;

									p {
										font-size: 14px;
										color: var(--color-txt);
										font-weight: 400;
										overflow: hidden;
										text-overflow: ellipsis;
										white-space: nowrap;
									}
								}
							}

							.item:last-child {
								border-bottom: none;
							}
						}
					}

					&:first-child {
						.category-header-container {
							.category-header {
								border-radius: 13px 13px 0 0;
								border-top: 1px solid var(--color-border-2);

								.category-chevron {
									border-top-right-radius: 13px;
								}
							}
						}
					}

					&:last-child {
						.category-header-container {
							.category-header {
								.category-chevron {
									border-bottom-right-radius: 13px;
								}
							}
						}
					}

				}

			}

		}
	}
}

</style>